import { createFileRoute } from "@tanstack/react-router";
import {
  Component,
  Suspense,
  type ReactNode,
  useEffect,
  useState,
} from "react";

import { CcoCarousel } from "~/components/cco-carousel";
import { CCOList } from "~/components/cco-list";
import { FeatureList } from "~/components/feature-list";
import { BuilderCard } from "~/components/helpers/builder-card";
import { BuilderLink } from "~/components/helpers/builder-link";
import { Centered } from "~/components/helpers/centered";
import { Columns } from "~/components/helpers/columns";
import { Container } from "~/components/helpers/container";
import { AnimatedChevrons } from "~/components/icons/animated-chevrons";
import { MembershipPlanDetails } from "~/components/membership-plan-details";
import { SavingsComparisonTable } from "~/components/savings-comparison-table";
import { StoriesCarousel } from "~/components/stories-carousel";
import { SummitRegistration } from "~/components/summit/summit-registration";
import { SummitRegistrationPopup } from "~/components/summit/summit-registration-popup";
import { AgentShowcase } from "~/components/ui/agent-showcase";
import { AuroraBackground } from "~/components/ui/aurora-background";
import { AuthorGrid } from "~/components/ui/author-grid";
import { Button } from "~/components/ui/button";
import { CenteredIconSection } from "~/components/ui/centered-icon-section";
import { CountdownBar } from "~/components/ui/countdown-bar";
import { CredibilityBand } from "~/components/ui/credibility-band";
import { CtaBanner } from "~/components/ui/cta-banner";
import { DailySummitAgenda } from "~/components/ui/daily-summit-agenda";
import { DoubleImageCarousel } from "~/components/ui/double-image-carousel";
import { FaqSection } from "~/components/ui/faq-section";
import { FeatureSection } from "~/components/ui/feature-section";
import { FeatureShowcase } from "~/components/ui/feature-showcase";
import { FullScreenCTA } from "~/components/ui/full-screen-cta";
// Components — direct imports for ones that are lightweight,
// the heavier/data-fetching ones are fine to import directly too
// since this is a noindex internal page.
import { Heading } from "~/components/ui/heading";
import { IconFeatureGrid } from "~/components/ui/icon-feature-grid";
import { ImageWithCaption } from "~/components/ui/image-with-caption";
import { LabelList } from "~/components/ui/label-list";
import { PathLadder } from "~/components/ui/path-ladder";
import { PricingCard } from "~/components/ui/pricing-card";
import { PricingComparison } from "~/components/ui/pricing-comparison";
import { PricingPlans } from "~/components/ui/pricing-plans";
import { ProcessSteps } from "~/components/ui/process-steps";
import { ProductShowcase } from "~/components/ui/product-showcase";
import { Prose } from "~/components/ui/prose";
import { ProseBlock } from "~/components/ui/prose-block";
import { TagList } from "~/components/ui/tag-list";
import { Testimonials } from "~/components/ui/testimonials";
import { TimeZoneOverview } from "~/components/ui/timezone-overview";
import { ValueStack } from "~/components/ui/value-stack";
import { cn } from "~/lib/cn";
import { MembershipPlan } from "~/lib/schemas/db-enums";
import { createBrandMeta } from "~/lib/shared/meta";

export const Route = createFileRoute("/_public/brand/components/")({
  component: BuilderComponentsPage,
  staticData: { title: "Components" },
  head: () =>
    createBrandMeta({
      title: "Builder Components | Dream Coach Match",
      description:
        "Visual reference of all custom Builder.io components available for page building",
      noindex: true,
    }),
});

const CATEGORIES = [
  { id: "layout", label: "Layout" },
  { id: "typography", label: "Typography & Content" },
  { id: "cards-lists", label: "Cards & Lists" },
  { id: "marketing", label: "Marketing & CTA" },
  { id: "media", label: "Media" },
  { id: "pricing", label: "Pricing" },
  { id: "testimonials", label: "Testimonials & Stories" },
  { id: "summit", label: "Summit & Events" },
  { id: "interactive", label: "Interactive" },
  { id: "backgrounds", label: "Backgrounds" },
  { id: "faq", label: "FAQ" },
] as const;

class ComponentErrorBoundary extends Component<
  { children: ReactNode },
  { error: Error | null }
> {
  state: { error: Error | null } = { error: null };

  static getDerivedStateFromError(error: Error) {
    return { error };
  }

  override render() {
    if (this.state.error) {
      return (
        <div className="rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-700">
          <p className="font-medium">Failed to render component</p>
          <p className="mt-1 text-red-600">{this.state.error.message}</p>
        </div>
      );
    }
    return this.props.children;
  }
}

function ComponentEntry({
  name,
  builderName,
  description,
  children,
  fullWidth = false,
}: {
  name: string;
  builderName: string;
  description: string;
  children: ReactNode;
  fullWidth?: boolean;
}) {
  return (
    <div className="space-y-3">
      <div>
        <div className="flex items-baseline gap-2">
          <h3 className="font-heading text-xl font-medium text-gray-900">
            {name}
          </h3>
          {builderName !== name && (
            <span className="text-sm text-muted-foreground">
              (Builder: {builderName})
            </span>
          )}
        </div>
        <p className="text-sm text-muted-foreground">{description}</p>
      </div>
      <div
        className={cn(
          "rounded-xl border border-gray-200 bg-white",
          !fullWidth && "p-6",
          fullWidth && "overflow-hidden",
        )}
      >
        <ComponentErrorBoundary>
          <Suspense
            fallback={
              <div className="flex h-24 items-center justify-center text-sm text-gray-400">
                Loading...
              </div>
            }
          >
            {children}
          </Suspense>
        </ComponentErrorBoundary>
      </div>
    </div>
  );
}

function CategorySection({
  id,
  title,
  children,
}: {
  id: string;
  title: string;
  children: ReactNode;
}) {
  return (
    <section id={id} className="scroll-mt-20 space-y-8">
      <h2 className="font-heading text-2xl font-medium tracking-tight text-gray-900 md:text-3xl">
        {title}
      </h2>
      {children}
    </section>
  );
}

function BuilderComponentsPage() {
  const [activeSection, setActiveSection] = useState<string>("layout");

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) {
            setActiveSection(entry.target.id);
          }
        }
      },
      { rootMargin: "-80px 0px -60% 0px", threshold: 0 },
    );

    for (const cat of CATEGORIES) {
      const el = document.getElementById(cat.id);
      if (el) observer.observe(el);
    }

    return () => observer.disconnect();
  }, []);

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-25 via-white to-gray-50">
      {/* Hero */}
      <div className="border-b border-gray-200 bg-white px-4 py-12 text-center">
        <h1 className="font-heading text-4xl font-medium tracking-tight text-gray-900 md:text-5xl">
          Builder.io Components
        </h1>
        <p className="mx-auto mt-4 max-w-2xl text-lg text-muted-foreground">
          Visual reference of every custom component available in the Builder.io
          editor. Use this page to see what each component looks like and when
          to use it.
        </p>
      </div>

      <div className="mx-auto flex max-w-screen-2xl gap-8 px-4 py-8">
        {/* Sidebar */}
        <nav className="sticky top-20 hidden h-fit w-56 shrink-0 space-y-1 lg:block">
          {CATEGORIES.map((cat) => (
            <a
              key={cat.id}
              href={`#${cat.id}`}
              className={cn(
                "block rounded-md px-3 py-1.5 text-sm transition-colors",
                activeSection === cat.id
                  ? "bg-purple-100 font-medium text-purple-700"
                  : "text-gray-600 hover:bg-gray-100 hover:text-gray-900",
              )}
            >
              {cat.label}
            </a>
          ))}
        </nav>

        {/* Main content */}
        <main className="min-w-0 flex-1 space-y-16">
          {/* Layout */}
          <CategorySection id="layout" title="Layout">
            <ComponentEntry
              name="Container"
              builderName="Container"
              description="Wraps content with consistent horizontal padding and max-width. Use for any content section that needs centered, bounded width."
            >
              <Container pagePadding maxWidth="lg">
                <div className="rounded-lg bg-purple-50 p-4 text-center text-sm text-purple-700">
                  Content inside a Container (max-width: lg, with page padding)
                </div>
              </Container>
            </ComponentEntry>

            <ComponentEntry
              name="Distributed Columns"
              builderName="Distributed Columns"
              description="Evenly distributes children into a responsive grid. Use for side-by-side content blocks."
            >
              <Columns count="3">
                <div className="rounded-lg bg-purple-50 p-4 text-center text-sm text-purple-700">
                  Column 1
                </div>
                <div className="rounded-lg bg-violet-50 p-4 text-center text-sm text-violet-700">
                  Column 2
                </div>
                <div className="rounded-lg bg-orange-50 p-4 text-center text-sm text-orange-700">
                  Column 3
                </div>
              </Columns>
            </ComponentEntry>

            <ComponentEntry
              name="Centered"
              builderName="Centered"
              description="Centers its children horizontally and vertically. Use for hero content or call-to-action blocks."
            >
              <Centered>
                <div className="rounded-lg bg-purple-50 px-8 py-4 text-center text-sm text-purple-700">
                  Centered content
                </div>
              </Centered>
            </ComponentEntry>

            <ComponentEntry
              name="Full Screen CTA"
              builderName="FullScreenCTA"
              description="Full-viewport section with wave background and glassmorphism card. Use for high-impact call-to-action moments. Accepts children for content."
              fullWidth
            >
              <div className="h-[400px] overflow-hidden">
                <FullScreenCTA className="!h-[400px]">
                  <div className="space-y-4 text-center">
                    <h2 className="font-heading text-2xl font-medium tracking-tight text-gray-900">
                      Ready to Transform?
                    </h2>
                    <p className="text-muted-foreground">
                      Join thousands of clients who found their perfect coach.
                    </p>
                    <Button variant="primary">Get Started</Button>
                  </div>
                </FullScreenCTA>
              </div>
            </ComponentEntry>
          </CategorySection>

          {/* Typography & Content */}
          <CategorySection id="typography" title="Typography & Content">
            <ComponentEntry
              name="Heading"
              builderName="Heading"
              description="Structured heading block with pre-headline, headline, subheadline, and description. The main way to add section titles."
            >
              <Heading
                preHeadline="Our Approach"
                headline="Transform Your Life with Expert Coaching"
                subheadline="Personalized guidance from certified professionals"
                description="We connect you with coaches who understand your goals and can help you achieve lasting transformation through proven methodologies."
              />
            </ComponentEntry>

            <ComponentEntry
              name="Prose"
              builderName="Prose"
              description="Renders rich text content from Builder with proper typography styling. Use for body copy and editorial content."
            >
              <Prose
                content="<p>This is a rich text block rendered with <strong>proper typography</strong>. It supports all standard HTML formatting including <em>italics</em>, <a href='#'>links</a>, lists, and more.</p><p>Use this component for any free-form content in your Builder pages.</p>"
                size="lg"
              />
            </ComponentEntry>

            <ComponentEntry
              name="Prose Block (SEO)"
              builderName="Prose Block (SEO)"
              description="Full-width prose section with headline and optional CTA button. Designed for SEO landing pages with longer content."
              fullWidth
            >
              <ProseBlock
                headline="Why Choose Dream Coach Match?"
                content="<p>Our platform uses AI-powered matching to connect you with coaches who truly understand your needs. With over 500 certified coaches across 30+ specializations, we ensure every match is meaningful.</p><ul><li>AI-powered coach matching</li><li>Verified certifications</li><li>Satisfaction guarantee</li></ul>"
                buttonText="Learn More"
                buttonHref="/about"
              />
            </ComponentEntry>
          </CategorySection>

          {/* Cards & Lists */}
          <CategorySection id="cards-lists" title="Cards & Lists">
            <ComponentEntry
              name="Card"
              builderName="Card"
              description="Flexible card container with optional title and description. Accepts children for custom content inside."
            >
              <Columns count="2">
                <BuilderCard
                  title="Getting Started"
                  description="Everything you need to begin your coaching journey."
                >
                  <p className="text-sm text-gray-600">
                    Card content goes here — any Builder components can be
                    nested inside.
                  </p>
                </BuilderCard>
                <BuilderCard
                  title="Advanced Features"
                  description="Unlock the full potential of your coaching practice."
                  fillHeight
                >
                  <p className="text-sm text-gray-600">
                    This card uses fillHeight to match its sibling.
                  </p>
                </BuilderCard>
              </Columns>
            </ComponentEntry>

            <ComponentEntry
              name="Feature List"
              builderName="Feature List"
              description="Checkmark list of features. Use on pricing pages, product descriptions, or anywhere you need a benefits list."
            >
              <FeatureList
                features={[
                  {
                    feature: "Unlimited coaching sessions",
                    description: "Connect as often as you need",
                  },
                  {
                    feature: "AI-powered matching",
                    description: "Find your perfect coach in minutes",
                  },
                  {
                    feature: "Progress tracking",
                    description: "Measure your growth over time",
                  },
                  {
                    feature: "Secure messaging",
                    description: "Stay in touch between sessions",
                  },
                ]}
              />
            </ComponentEntry>

            <ComponentEntry
              name="Tag List"
              builderName="Tag List"
              description="Displays coaching offer tags with icons. Use to show session details like duration, format, and delivery method."
            >
              <TagList
                tags={[
                  {
                    id: "1",
                    title: "12 Sessions",
                    property: "Number of sessions",
                    icon: "calendar",
                  },
                  {
                    id: "2",
                    title: "60 minutes",
                    property: "Session duration",
                    icon: "clock",
                  },
                  {
                    id: "3",
                    title: "Video Call",
                    property: "Delivery method",
                    icon: "globe",
                  },
                  {
                    id: "4",
                    title: "1-on-1",
                    property: "Session format",
                    icon: "users",
                  },
                ]}
              />
            </ComponentEntry>

            <ComponentEntry
              name="Label List"
              builderName="Label List"
              description="A row of label badges. Use for displaying categories, tags, or filter options."
            >
              <LabelList
                labels={[
                  { id: "1", text: "Life Coaching" },
                  { id: "2", text: "Career", variant: "secondary" },
                  { id: "3", text: "Mindfulness", variant: "outline" },
                  { id: "4", text: "Leadership" },
                  { id: "5", text: "Health & Wellness", variant: "secondary" },
                ]}
                gap="md"
              />
            </ComponentEntry>

            <ComponentEntry
              name="Author Grid"
              builderName="Author Grid"
              description="Grid of author/speaker cards with image, name, title, and bio. Use for team pages, speaker lineups, or contributor lists."
            >
              <AuthorGrid
                authors={[
                  {
                    id: "1",
                    name: "Sarah Mitchell",
                    image:
                      "https://images.unsplash.com/photo-1494790108377-be9c29b29330?w=200&h=200&fit=crop",
                    title: "Executive Coach",
                    subtitle: "ICF PCC Certified",
                  },
                  {
                    id: "2",
                    name: "James Chen",
                    image:
                      "https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=200&h=200&fit=crop",
                    title: "Leadership Coach",
                    subtitle: "20+ years experience",
                  },
                  {
                    id: "3",
                    name: "Maria Rodriguez",
                    image:
                      "https://images.unsplash.com/photo-1438761681033-6461ffad8d80?w=200&h=200&fit=crop",
                    title: "Wellness Coach",
                    subtitle: "Holistic approach",
                  },
                ]}
                columns="3"
                imageSize="md"
              />
            </ComponentEntry>
          </CategorySection>

          {/* Marketing & CTA */}
          <CategorySection id="marketing" title="Marketing & CTA">
            <ComponentEntry
              name="CTA Banner"
              builderName="CTA Banner"
              description="Dark gradient banner with animated glow rings and CTA button. Use for high-impact conversion sections."
              fullWidth
            >
              <CtaBanner
                headline="Start Your Coaching Journey Today"
                description="Join thousands of clients who've transformed their lives with expert coaching. Your first session is complimentary."
                buttonText="Find Your Coach"
                buttonHref="/discovery"
              />
            </ComponentEntry>

            <ComponentEntry
              name="Feature Section"
              builderName="FeatureSection"
              description="Two-column section with text on one side and an image placeholder on the other. Supports left/right layout and active/inactive styling."
              fullWidth
            >
              <FeatureSection
                preHeadline="How It Works"
                headline="AI-Powered Coach Matching"
                description="Our proprietary algorithm analyzes your goals, preferences, and personality to find coaches who are the perfect fit. No more guessing — just meaningful connections."
                layout="left"
                look="active"
              />
            </ComponentEntry>

            <ComponentEntry
              name="Feature Showcase"
              builderName="Feature Showcase"
              description="Two-column layout with text + visual slot. Alternate direction with the reverse prop for visual rhythm. Drop any content into the visual side."
              fullWidth
            >
              <FeatureShowcase
                preHeadline="Platform Feature"
                headline="Track Your Transformation"
                description="Set goals, log sessions, and visualize your progress over time. Our dashboard gives you clarity on how far you've come and where you're headed."
                ctaText="See How It Works"
                ctaHref="/features"
              >
                <div className="flex h-48 w-full items-center justify-center rounded-xl bg-gradient-to-br from-purple-100 to-violet-100 text-sm text-purple-600">
                  Visual content slot (drop images or components here in
                  Builder)
                </div>
              </FeatureShowcase>
            </ComponentEntry>

            <ComponentEntry
              name="Icon Feature Grid"
              builderName="Icon Feature Grid"
              description="Grid of feature cards with icons, titles, and descriptions. Supports 2/3/4 columns and muted/plain/dark tones."
              fullWidth
            >
              <IconFeatureGrid
                preHeadline="Why Choose Us"
                headline="Everything You Need to Succeed"
                description="Our platform provides the tools and support for meaningful coaching relationships."
                columns={3}
                tone="muted"
                items={[
                  {
                    title: "Smart Matching",
                    description:
                      "AI analyzes 50+ factors to find your ideal coach match.",
                    iconName: "brain",
                  },
                  {
                    title: "Verified Coaches",
                    description:
                      "Every coach is credential-verified and background-checked.",
                    iconName: "shield-check",
                  },
                  {
                    title: "Progress Tracking",
                    description:
                      "Visual dashboards to measure your growth and milestones.",
                    iconName: "barChart",
                  },
                ]}
              />
            </ComponentEntry>

            <ComponentEntry
              name="Centered Icon Section"
              builderName="Centered Icon Section"
              description="Centered headline with icon feature cards below. Supports purple/slate/plain tones and 2/3/4 columns."
              fullWidth
            >
              <CenteredIconSection
                preHeadline="Our Values"
                headline="Built on Trust"
                description="Every aspect of our platform is designed around transparency and integrity."
                tone="purple"
                columns={3}
                items={[
                  {
                    iconName: "heart",
                    label: "Empathy",
                    description:
                      "We understand that coaching is a deeply personal journey.",
                  },
                  {
                    iconName: "shield-check",
                    label: "Trust",
                    description:
                      "Verified credentials and transparent reviews.",
                  },
                  {
                    iconName: "sparkles",
                    label: "Growth",
                    description:
                      "Celebrating every step of your transformation.",
                  },
                ]}
              />
            </ComponentEntry>

            <ComponentEntry
              name="Path Ladder"
              builderName="Path Ladder (choose your tier)"
              description="'The path' hub for For-Coaches — Free → NOVA → Coach Business Builder as connected rungs, each with its own CTA. One rung can be featured. Fully Builder-editable."
              fullWidth
            >
              <PathLadder
                preHeadline="One path"
                headline="Start free. Climb as far as you want."
                description="Every coach starts in the same place. You decide how much help you bring in."
                steps={[
                  {
                    eyebrow: "Step 1 · Free",
                    title: "Free",
                    description: "Get verified, get listed, run your clients.",
                    items: [
                      { text: "Verified coaching profile in the marketplace" },
                      { text: "Run your own clients in the Coaching OS" },
                      { text: "A free NOVA diagnostic to name your next move" },
                    ],
                    ctaText: "Create free account",
                  },
                  {
                    eyebrow: "Step 2 · NOVA",
                    title: "NOVA",
                    description: "The AI that builds your coaching business.",
                    items: [
                      { text: "Diagnose, plan, build, and refine with NOVA" },
                      {
                        text: "Articles, outreach, profile — drafted in your voice",
                      },
                      { text: "Lite $50 · Pro $150 · Scale $299 a month" },
                    ],
                    ctaText: "Explore NOVA",
                    featured: true,
                  },
                  {
                    eyebrow: "Step 3 · Coach Business Builder",
                    title: "Coach Business Builder",
                    description: "Want it built for you? Bring in our team.",
                    items: [
                      { text: "A group accelerator to build alongside peers" },
                      { text: "Or a done-for-you build by our strategists" },
                      { text: "Human help, on top of everything NOVA does" },
                    ],
                    ctaText: "Book a call",
                  },
                ]}
              />
            </ComponentEntry>

            <ComponentEntry
              name="Process Steps"
              builderName="Process Steps"
              description="Numbered, connected stepper for an ordered narrative. Each step takes an icon, title, description, and an optional badge (e.g. 'Coming soon' for a not-yet-shipped step)."
              fullWidth
            >
              <ProcessSteps
                preHeadline="How NOVA works"
                headline="Four steps from blank page to booked clients"
                description="NOVA walks your coaching business forward one stage at a time."
                tone="purple"
                steps={[
                  {
                    iconName: "search",
                    title: "Diagnose",
                    description:
                      "Define your ideal client and pressure-test the niche so you build on solid ground.",
                    badge: "Live",
                  },
                  {
                    iconName: "compass",
                    title: "Plan",
                    description:
                      "Shape your offer, pricing, and 'I help…' positioning into a clear go-to-market.",
                    badge: "Live",
                  },
                  {
                    iconName: "rocket",
                    title: "Build",
                    description:
                      "Turn the plan into assets — profile, articles, LinkedIn, outreach, and more.",
                    badge: "Live",
                  },
                  {
                    iconName: "gauge",
                    title: "Refine",
                    description:
                      "Reflect on real sessions and sharpen your practice as you grow.",
                    badge: "Coming soon",
                  },
                ]}
              />
            </ComponentEntry>
          </CategorySection>

          {/* Media */}
          <CategorySection id="media" title="Media">
            <ComponentEntry
              name="Product Showcase"
              builderName="Product Showcase (video / annotated)"
              description="The coach's working environment as an embedded video or an annotated screenshot. Numbered hotspots stay in sync with the legend below. Falls back to a placeholder until real media is added."
              fullWidth
            >
              <ProductShowcase
                preHeadline="Your coaching OS"
                headline="Everything you need to run clients, in one place"
                description="Profiles, sessions, goals, and homework — the workspace your clients log into."
                aspect="16/9"
                hotspots={[
                  {
                    x: 22,
                    y: 30,
                    label: "Client roster",
                    description:
                      "Every active client at a glance, with status and next session.",
                  },
                  {
                    x: 62,
                    y: 24,
                    label: "Shared goals",
                    description:
                      "Set and track goals together — visible to you and the client.",
                  },
                  {
                    x: 40,
                    y: 66,
                    label: "Homework & notes",
                    description:
                      "Assign homework, capture session notes, keep it all in context.",
                  },
                  {
                    x: 80,
                    y: 70,
                    label: "Secure messaging",
                    description: "Stay in touch between sessions, privately.",
                  },
                ]}
              />
            </ComponentEntry>

            <ComponentEntry
              name="Double Image Carousel"
              builderName="Double Image Carousel"
              description="Two-row auto-scrolling image carousel with configurable speed, hover pause, and edge masks. Use for social proof or visual galleries."
              fullWidth
            >
              <DoubleImageCarousel
                images={[
                  {
                    src: "https://images.unsplash.com/photo-1522202176988-66273c2fd55f?w=400&h=260&fit=crop",
                    alt: "Coaching session",
                  },
                  {
                    src: "https://images.unsplash.com/photo-1552664730-d307ca884978?w=400&h=260&fit=crop",
                    alt: "Team workshop",
                  },
                  {
                    src: "https://images.unsplash.com/photo-1573497019940-1c28c88b4f3e?w=400&h=260&fit=crop",
                    alt: "Professional coach",
                  },
                  {
                    src: "https://images.unsplash.com/photo-1600880292203-757bb62b4baf?w=400&h=260&fit=crop",
                    alt: "Business meeting",
                  },
                  {
                    src: "https://images.unsplash.com/photo-1531545514256-b1400bc00f31?w=400&h=260&fit=crop",
                    alt: "Workshop",
                  },
                  {
                    src: "https://images.unsplash.com/photo-1517245386807-bb43f82c33c4?w=400&h=260&fit=crop",
                    alt: "Presentation",
                  },
                ]}
                rowHeight={200}
                speedPxPerSecond={25}
                showSecondRow
                maskEdges
                rounded="xl"
              />
            </ComponentEntry>

            <ComponentEntry
              name="Image with Caption"
              builderName="Image with Caption"
              description="Wraps any image or visual content with a styled caption below. Accepts children for the image slot."
            >
              <ImageWithCaption caption="A coaching session in progress — our platform facilitates meaningful connections between coaches and clients.">
                <img
                  src="https://images.unsplash.com/photo-1522202176988-66273c2fd55f?w=600&h=300&fit=crop"
                  alt="Coaching session"
                  className="w-full rounded"
                />
              </ImageWithCaption>
            </ComponentEntry>

            <ComponentEntry
              name="Animated Chevrons"
              builderName="Animated Chevrons"
              description="Three animated bouncing chevrons. Use to draw attention to content below, typically after a hero section."
            >
              <div className="flex justify-center py-4 text-purple-600">
                <AnimatedChevrons size="lg" />
              </div>
            </ComponentEntry>
          </CategorySection>

          {/* Pricing */}
          <CategorySection id="pricing" title="Pricing">
            <ComponentEntry
              name="Pricing Plans"
              builderName="Pricing Plans (monthly/annual)"
              description="Free / Lite / Pro / Scale grid with a shared monthly/annual toggle that rewrites every price. One card can be 'featured'. Plan contents are placeholders — fully Builder-editable."
              fullWidth
            >
              <PricingPlans
                preHeadline="Plans for coaches"
                headline="Pick how far up you want to go"
                description="Start free. Add NOVA when you're ready to build the business."
                plans={[
                  {
                    name: "Free",
                    who: "For certified coaches exploring what's possible.",
                    monthlyPrice: "$0",
                    annualPrice: "$0",
                    priceSuffix: "/forever",
                    billedMonthly: "No card required",
                    billedAnnual: "No card required",
                    ctaText: "Create free account",
                    features: [
                      {
                        group: "NOVA AI",
                        text: "Diagnose your business & plan your strategy, ICP and niche",
                      },
                      { group: "NOVA AI", text: "5 free creations to start" },
                      {
                        group: "Platform",
                        text: "Coaching profile (by approval)",
                      },
                      {
                        group: "Platform",
                        text: "Client workspace — up to 3 clients",
                      },
                      { group: "Platform", text: "Community access" },
                    ],
                  },
                  {
                    name: "Lite",
                    who: "For coaches under $50K building their first system.",
                    monthlyPrice: "$50",
                    annualPrice: "$42",
                    priceSuffix: "/mo",
                    billedMonthly: "Billed monthly",
                    billedAnnual: "$498 billed annually",
                    ctaText: "Choose Lite",
                    features: [
                      {
                        group: "NOVA AI",
                        text: "30 creations a month — posts, scripts, articles & more",
                      },
                      {
                        group: "NOVA AI",
                        text: "Full Diagnose → Plan → Build → Refine",
                      },
                      {
                        group: "Authority & Platform",
                        text: "1 published authority article / month",
                      },
                      {
                        group: "Authority & Platform",
                        text: "Offer the DCM Mini Sprint™",
                      },
                      {
                        group: "Authority & Platform",
                        text: "Client workspace — 5 active clients",
                      },
                    ],
                  },
                  {
                    name: "Pro",
                    who: "For coaches $50–100K ready to grow with a real engine.",
                    monthlyPrice: "$150",
                    annualPrice: "$125",
                    priceSuffix: "/mo",
                    billedMonthly: "Billed monthly",
                    billedAnnual: "$1,494 billed annually",
                    ctaText: "Choose Pro",
                    badge: "Most popular",
                    featured: true,
                    inherits: "Everything in Lite, plus —",
                    features: [
                      {
                        group: "NOVA AI + Expertise",
                        text: "120 creations a month",
                      },
                      {
                        group: "NOVA AI + Expertise",
                        text: "AI Coach Assistant — MPAI by Margaret Moore",
                      },
                      {
                        group: "Grow",
                        text: "3 published articles / month + Pro search badge",
                      },
                      {
                        group: "Grow",
                        text: "Full marketing suite — CRM, email, SMS, funnels, booking",
                      },
                      { group: "Grow", text: "25 active clients" },
                    ],
                  },
                  {
                    name: "Scale",
                    who: "For 6-figure coaches ready to scale and lead.",
                    monthlyPrice: "$299",
                    annualPrice: "$248",
                    priceSuffix: "/mo",
                    billedMonthly: "Billed monthly",
                    billedAnnual: "$2,978 billed annually",
                    ctaText: "Choose Scale",
                    inherits: "Everything in Pro, plus —",
                    features: [
                      {
                        group: "NOVA AI + Certification",
                        text: "Unlimited creations (fair use)",
                      },
                      {
                        group: "NOVA AI + Certification",
                        text: "Become MPB-certified — trained by Margaret Moore",
                      },
                      {
                        group: "Scale",
                        text: "Unlimited published articles + featured placement",
                      },
                      {
                        group: "Scale",
                        text: "Host your own community & group programs",
                      },
                      {
                        group: "Scale",
                        text: "Unlimited clients · priority support",
                      },
                    ],
                  },
                ]}
              />
            </ComponentEntry>

            <ComponentEntry
              name="Pricing Comparison Table"
              builderName="Pricing Comparison Table (collapsible)"
              description="Collapsible 'every feature, side by side' matrix. Cells render ✓ / — / plain text, or *asterisks* to emphasize. Category rows span all columns. Shown open here; defaults to collapsed."
              fullWidth
            >
              <PricingComparison
                preHeadline="Compare in full"
                headline="Every feature, side by side"
                defaultOpen
                columns={[
                  { label: "Free" },
                  { label: "Lite" },
                  { label: "Pro" },
                  { label: "Scale" },
                ]}
                rows={[
                  { label: "NOVA AI", isCategory: true },
                  {
                    label: "NOVA creations / month",
                    cells: [
                      { value: "5" },
                      { value: "30" },
                      { value: "120" },
                      { value: "*Unlimited*" },
                    ],
                  },
                  {
                    label: "Diagnose your business",
                    cells: [
                      { value: "✓" },
                      { value: "✓" },
                      { value: "✓" },
                      { value: "✓" },
                    ],
                  },
                  {
                    label: "Build assets (content, scripts, profile)",
                    cells: [
                      { value: "Trial only" },
                      { value: "✓" },
                      { value: "✓" },
                      { value: "✓" },
                    ],
                  },
                  {
                    label: "AI Coach Assistant — MPAI",
                    cells: [
                      { value: "Trial" },
                      { value: "—" },
                      { value: "✓" },
                      { value: "✓" },
                    ],
                  },
                  { label: "Published Authority Content", isCategory: true },
                  {
                    label: "Authority articles published",
                    cells: [
                      { value: "—" },
                      { value: "1 / mo" },
                      { value: "3 / mo" },
                      { value: "Unlimited" },
                    ],
                  },
                  {
                    label: "Search visibility",
                    cells: [
                      { value: "Standard" },
                      { value: "Standard" },
                      { value: "*Pro badge*" },
                      { value: "*Featured + badge*" },
                    ],
                  },
                  { label: "Coach Operating System", isCategory: true },
                  {
                    label: "Active clients",
                    cells: [
                      { value: "3" },
                      { value: "5" },
                      { value: "25" },
                      { value: "*Unlimited*" },
                    ],
                  },
                  {
                    label: "Notes, goals, homework, secure messaging",
                    cells: [
                      { value: "✓" },
                      { value: "✓" },
                      { value: "✓" },
                      { value: "✓" },
                    ],
                  },
                  {
                    label: "Marketing Tools (powered by GoHighLevel)",
                    isCategory: true,
                  },
                  {
                    label: "CRM, email, SMS, newsletter, social",
                    cells: [
                      { value: "—" },
                      { value: "—" },
                      { value: "✓" },
                      { value: "✓" },
                    ],
                  },
                  { label: "Support", isCategory: true },
                  {
                    label: "Support channel",
                    cells: [
                      { value: "—" },
                      { value: "Email / Community" },
                      { value: "Email / Community" },
                      { value: "Email + Private Slack" },
                    ],
                  },
                ]}
              />
            </ComponentEntry>

            <ComponentEntry
              name="Value Stack"
              builderName="Value Stack (what it replaces)"
              description="'What it replaces' cost-stack cards with running totals and an emphasized footnote. Fully Builder-editable."
              fullWidth
            >
              <ValueStack
                preHeadline="What it replaces"
                headline="One subscription. The whole stack."
                description="DCM doesn't just give you marketing software. It replaces the strategist, the copywriter, and the toolset most coaches pay for separately."
                columns={[
                  {
                    title: "Your strategy & content spend",
                    items: [
                      {
                        label: "AI writing tool (ChatGPT Plus)",
                        amount: "$20/mo",
                      },
                      {
                        label: "LinkedIn ghostwriter / copywriter",
                        amount: "$500–2,000/mo",
                      },
                      {
                        label: "Business strategy consultant",
                        amount: "$300+/session",
                      },
                      {
                        label: "SEO / content research tool",
                        amount: "$99/mo",
                      },
                    ],
                    totalLabel: "NOVA replaces it",
                    totalValue: "from $50/mo",
                  },
                  {
                    title: "Your marketing stack",
                    items: [
                      { label: "CRM (HubSpot, Pipedrive)", amount: "$99/mo" },
                      {
                        label: "Email + SMS (Mailchimp, Twilio)",
                        amount: "$126/mo",
                      },
                      {
                        label: "Course platform (Kajabi, Teachable)",
                        amount: "$99/mo",
                      },
                      {
                        label: "Funnels, booking, community tools",
                        amount: "$227/mo",
                      },
                    ],
                    totalLabel: "Pro includes it all",
                    totalValue: "$150/mo",
                  },
                ]}
                footnote="A comparable stack runs *$550+/month* — before you've written a single post or made a strategic decision."
              />
            </ComponentEntry>

            <ComponentEntry
              name="Credibility Band"
              builderName="Credibility Band (dark)"
              description="Centered dark 'why we're different' band with eyebrow, headline, paragraph, and optional CTA. Reusable beyond pricing."
              fullWidth
            >
              <CredibilityBand
                preHeadline="Why NOVA is different"
                headline="Built on licensed coaching expertise"
                description="NOVA isn't generic AI wearing a coaching costume. Its strategy and assistant capabilities are grounded in the MP framework by Margaret Moore — co-founder of the Institute of Coaching. Every article you publish carries that authority."
              />
            </ComponentEntry>

            <ComponentEntry
              name="Pricing Card"
              builderName="PricingCard"
              description="Membership pricing card with plan badge, price, description, and feature list. Use the highlighted prop for the recommended plan."
            >
              <Columns count="3">
                <PricingCard
                  membershipPlan={MembershipPlan.Standard}
                  price="Free"
                  description="Get started with basic coaching tools"
                >
                  <FeatureList
                    size="sm"
                    features={[
                      { feature: "Coach matching", description: "" },
                      { feature: "Basic profile", description: "" },
                      { feature: "Messaging", description: "" },
                    ]}
                  />
                </PricingCard>
                <PricingCard
                  membershipPlan={MembershipPlan.Pro}
                  price="$29"
                  priceSuffix="/month"
                  description="For coaches ready to grow"
                  highlighted
                >
                  <FeatureList
                    size="sm"
                    features={[
                      { feature: "Everything in Standard", description: "" },
                      { feature: "Priority matching", description: "" },
                      { feature: "Analytics dashboard", description: "" },
                    ]}
                  />
                </PricingCard>
                <PricingCard
                  membershipPlan={MembershipPlan.ProPlus}
                  price="$79"
                  priceSuffix="/month"
                  description="Maximum visibility and tools"
                >
                  <FeatureList
                    size="sm"
                    features={[
                      { feature: "Everything in Pro", description: "" },
                      { feature: "Featured placement", description: "" },
                      { feature: "Custom branding", description: "" },
                    ]}
                  />
                </PricingCard>
              </Columns>
            </ComponentEntry>

            <ComponentEntry
              name="Plan Details Table"
              builderName="Plan Details Table (expandable)"
              description="Expandable comparison table showing detailed feature differences between membership plans."
            >
              <MembershipPlanDetails />
            </ComponentEntry>

            <ComponentEntry
              name="Savings Comparison Table"
              builderName="SavingsComparisonTable"
              description="Table comparing software costs and savings across membership plans. Shows the value proposition of bundled tools."
            >
              <SavingsComparisonTable />
            </ComponentEntry>
          </CategorySection>

          {/* Testimonials & Stories */}
          <CategorySection id="testimonials" title="Testimonials & Stories">
            <ComponentEntry
              name="Testimonials"
              builderName="Testimonials"
              description="Carousel of client testimonials with star ratings and navigation. Use for social proof on landing pages."
            >
              <Testimonials
                testimonials={[
                  {
                    author: "Emily Thompson",
                    role: "Marketing Director",
                    title: "Life-changing experience",
                    content:
                      "My coach helped me navigate a career transition with clarity and confidence. I went from feeling stuck to leading a team of 20.",
                    setHTML: false,
                  },
                  {
                    author: "Michael Park",
                    role: "Startup Founder",
                    title: "Best investment I've made",
                    content:
                      "The AI matching was spot-on. My coach understood my entrepreneurial challenges from day one. Revenue grew 3x in 6 months.",
                    setHTML: false,
                  },
                  {
                    author: "Lisa Martinez",
                    role: "Senior Engineer",
                    title: "Found my leadership voice",
                    content:
                      "I was promoted to Staff Engineer within a year of starting coaching. The structured approach to goal-setting made all the difference.",
                    setHTML: false,
                  },
                ]}
              />
            </ComponentEntry>

            <ComponentEntry
              name="Stories Carousel"
              builderName="Stories Carousel"
              description="Carousel of transformation stories from the database. Fetches real stories automatically — no configuration needed."
            >
              <StoriesCarousel />
            </ComponentEntry>

            <ComponentEntry
              name="CCO Carousel"
              builderName="CCO Carousel"
              description="Carousel of Chief Coaching Officers. Fetches CCO data from the database automatically."
            >
              <CcoCarousel />
            </ComponentEntry>

            <ComponentEntry
              name="CCO List"
              builderName="CCO List"
              description="Grid list of Chief Coaching Officers. Fetches CCO data from the database automatically."
            >
              <CCOList />
            </ComponentEntry>
          </CategorySection>

          {/* Summit & Events */}
          <CategorySection id="summit" title="Summit & Events">
            <ComponentEntry
              name="Countdown Bar"
              builderName="Countdown Bar"
              description="Sticky countdown timer bar with CTA button. Fetches the next summit start time automatically, falls back to the configured target date."
              fullWidth
            >
              <div className="relative">
                <CountdownBar
                  targetDate={new Date(
                    Date.now() + 7 * 24 * 60 * 60 * 1000,
                  ).toISOString()}
                  ctaText="Register for Free"
                  ctaLink="/summit"
                  className="!sticky-none !relative"
                />
              </div>
            </ComponentEntry>

            <ComponentEntry
              name="Time Zone Overview"
              builderName="Time Zone Overview"
              description="Shows event times across multiple time zones with country flags. Use on event landing pages."
            >
              <TimeZoneOverview
                title="Attend free via Zoom"
                dateRange="Friday, 25th - Sunday, 27th July 2025"
                timeZones={[
                  {
                    flagImage: "us.svg",
                    timeRange: "8am - 12pm",
                    city: "Los Angeles",
                  },
                  {
                    flagImage: "us.svg",
                    timeRange: "11am - 3pm",
                    city: "New York",
                  },
                  {
                    flagImage: "gb.svg",
                    timeRange: "4pm - 8pm",
                    city: "London",
                  },
                  {
                    flagImage: "ae.svg",
                    timeRange: "7pm - 11pm",
                    city: "Dubai",
                  },
                ]}
              />
            </ComponentEntry>

            <ComponentEntry
              name="Daily Summit Agenda"
              builderName="Daily Summit Agenda"
              description="Fetches and displays the summit session schedule grouped by day. Pulls data from the database automatically."
            >
              <DailySummitAgenda title="Summit Agenda" />
            </ComponentEntry>

            <ComponentEntry
              name="Summit Registration"
              builderName="Summit Registration"
              description="Registration form for summit events. Checks available summit options and allows users to register."
            >
              <SummitRegistration />
            </ComponentEntry>

            <ComponentEntry
              name="Summit Registration Popup"
              builderName="Summit Registration Popup"
              description="Floating popup version of the summit registration form. Appears as an overlay."
            >
              <SummitRegistrationPopup />
            </ComponentEntry>

            <ComponentEntry
              name="Summit Live Stream"
              builderName="Summit Live Stream"
              description="Embedded YouTube live stream player. Auto-detects the active session or use overrideVideoId for testing."
            >
              <div className="flex h-48 items-center justify-center rounded-xl bg-gray-100 text-sm text-gray-500">
                Embeds a YouTube live stream player. Auto-detects the active
                summit session, or use the overrideVideoId prop for
                testing/rehearsals.
              </div>
            </ComponentEntry>
          </CategorySection>

          {/* Interactive */}
          <CategorySection id="interactive" title="Interactive">
            <ComponentEntry
              name="Button"
              builderName="DCMButton"
              description="The main button component. Supports multiple variants and sizes. Accepts children for custom label content."
            >
              <div className="flex flex-wrap gap-3">
                <Button variant="default">Default</Button>
                <Button variant="primary">Primary</Button>
                <Button variant="fancy">Fancy</Button>
                <Button variant="ghost">Ghost</Button>
                <Button variant="destructive">Destructive</Button>
                <Button variant="link">Link</Button>
                <Button variant="pro">Pro</Button>
              </div>
              <div className="mt-4 flex flex-wrap items-center gap-3">
                <Button size="sm">Small</Button>
                <Button size="default">Default</Button>
                <Button size="lg">Large</Button>
                <Button size="xl">Extra Large</Button>
              </div>
            </ComponentEntry>

            <ComponentEntry
              name="Link"
              builderName="Link"
              description="Smart link wrapper — uses TanStack Router for internal links and standard anchor for external. Accepts children."
            >
              <div className="flex gap-4">
                <BuilderLink href="/discovery">
                  <span className="text-purple-700 underline">
                    Internal link to /discovery
                  </span>
                </BuilderLink>
                <BuilderLink href="https://example.com" target="_blank">
                  <span className="text-purple-700 underline">
                    External link (new tab)
                  </span>
                </BuilderLink>
              </div>
            </ComponentEntry>

            <ComponentEntry
              name="Agent Showcase"
              builderName="Agent Showcase (interactive)"
              description="Click-through explorer with a task sidebar, a pulsing 'click to explore' nudge, and a per-screen caption. Slides fall back to styled placeholders until real screenshots are added."
              fullWidth
            >
              <AgentShowcase
                preHeadline="Meet NOVA"
                headline="One AI, working across your whole business"
                description="Click through to see what NOVA does at each stage."
                slides={[
                  {
                    label: "Define your ideal client",
                    group: "Diagnose",
                    iconName: "search",
                    caption:
                      "NOVA interviews you and builds a sharp ideal-client profile — who you serve, their triggers, and what they'll pay for.",
                    messages: [
                      {
                        role: "nova",
                        text: "Who do you light up working with — and what's the expensive problem they're sitting on?",
                      },
                      {
                        role: "user",
                        text: "Burned-out senior managers who want out but feel trapped.",
                      },
                      {
                        role: "nova",
                        text: "Strong start. I'll draft an ICP around the 'trapped high-earner' — their triggers, objections, and where they cluster.",
                      },
                    ],
                  },
                  {
                    label: "Shape your offer & pricing",
                    group: "Plan",
                    iconName: "compass",
                    caption:
                      "It turns that profile into a structured offer, an entry product, and pricing you can actually say out loud.",
                    messages: [
                      {
                        role: "user",
                        text: "I have no idea what to charge for a 12-week program.",
                      },
                      {
                        role: "nova",
                        text: "Let's anchor on the outcome, not the hours. For this ICP I'd structure a $4k core program with a low-risk entry offer in front.",
                      },
                      {
                        role: "user",
                        text: "An entry offer — like what?",
                      },
                      {
                        role: "nova",
                        text: "A paid 5-day Mini Sprint. It proves value fast and converts into the full program. Here's the structure…",
                      },
                    ],
                  },
                  {
                    label: "Position yourself",
                    group: "Plan",
                    iconName: "target",
                    caption:
                      "NOVA writes your 'I help…' statement and a positioning summary that carries through everything you publish.",
                    messages: [
                      {
                        role: "nova",
                        text: 'Here\'s your positioning: "I help trapped senior managers design a credible exit — without torching their income."',
                      },
                      {
                        role: "user",
                        text: "That's exactly it. Can it carry into my profile?",
                      },
                      {
                        role: "nova",
                        text: "Yes — I'll reuse this line across your profile, LinkedIn headline, and every article so your message stays consistent.",
                      },
                    ],
                  },
                  {
                    label: "Draft an article",
                    group: "Build",
                    iconName: "lightbulb",
                    caption:
                      "Pick a challenge and NOVA drafts a credibility-building article — your expertise, your voice.",
                    messages: [
                      {
                        role: "user",
                        text: "Draft something on 'the golden handcuffs'.",
                      },
                      {
                        role: "nova",
                        text: "On it. I'll open with the 3am math every trapped earner runs, then reframe the exit as a design problem. Drafting in your voice now…",
                      },
                      {
                        role: "nova",
                        text: "Done — 900 words, ready for your review and one-click publish.",
                      },
                    ],
                  },
                  {
                    label: "Write outreach scripts",
                    group: "Build",
                    iconName: "message",
                    caption:
                      "It builds your LinkedIn outreach pack — opener, follow-ups, and targeting — from your strategy.",
                    messages: [
                      {
                        role: "user",
                        text: "I freeze every time I try to DM someone.",
                      },
                      {
                        role: "nova",
                        text: "You won't write these cold. Here's an opener, two follow-ups, and a LinkedIn search that surfaces your exact ICP.",
                      },
                    ],
                  },
                  {
                    label: "Reflect on a session",
                    group: "Refine",
                    iconName: "gauge",
                    caption:
                      "NOVA helps you reflect on real client sessions and sharpen your practice as you grow.",
                    messages: [
                      {
                        role: "user",
                        text: "My client keeps rescheduling. I'm not sure why.",
                      },
                      {
                        role: "nova",
                        text: "Let's look at the pattern. When did the cancellations start relative to the program milestones?",
                      },
                      {
                        role: "user",
                        text: "Right after we set the big goal.",
                      },
                      {
                        role: "nova",
                        text: "That often signals overwhelm, not disinterest. Try shrinking the next commitment — here's how I'd reframe it.",
                      },
                    ],
                  },
                ]}
              />
            </ComponentEntry>
          </CategorySection>

          {/* Backgrounds */}
          <CategorySection id="backgrounds" title="Backgrounds">
            <ComponentEntry
              name="Aurora Background"
              builderName="Aurora Background"
              description="Animated gradient aurora effect behind content. Use for hero sections or special landing pages. Accepts children."
              fullWidth
            >
              <div className="h-64 overflow-hidden">
                <AuroraBackground className="h-64">
                  <div className="relative z-10 flex h-full items-center justify-center">
                    <p className="font-heading text-2xl font-medium text-gray-900">
                      Content over Aurora
                    </p>
                  </div>
                </AuroraBackground>
              </div>
            </ComponentEntry>
          </CategorySection>

          {/* FAQ */}
          <CategorySection id="faq" title="FAQ">
            <ComponentEntry
              name="FAQ Section"
              builderName="FAQ Section"
              description="Full-width accordion FAQ section with optional headline and description. Use on landing pages and help sections."
              fullWidth
            >
              <FaqSection
                headline="Frequently Asked Questions"
                description="Everything you need to know about our coaching platform."
                items={[
                  {
                    question: "How does coach matching work?",
                    answer:
                      "Our AI analyzes your goals, preferences, communication style, and personality traits to match you with coaches who are the best fit. The algorithm considers over 50 factors to ensure meaningful connections.",
                  },
                  {
                    question: "Are the coaches certified?",
                    answer:
                      "Yes, every coach on our platform is credential-verified. We check ICF certifications, relevant training programs, and professional experience before approving coach profiles.",
                  },
                  {
                    question: "What if I'm not satisfied with my match?",
                    answer:
                      "We offer a satisfaction guarantee. If your first match isn't right, we'll re-match you at no additional cost. Our goal is to find the perfect coaching relationship for you.",
                  },
                ]}
              />
            </ComponentEntry>
          </CategorySection>
        </main>
      </div>
    </div>
  );
}
